import type {
  SamanthaPageContext,
} from '@/hooks/useSamanthaPageContext'

import type {
  EncounterContributionType,
} from '@/components/community/feed/types'

export type ContextualComposerMode =
  | 'samantha'
  | 'hybrid'

export type ContextualComposerConfig = {
  mode: ContextualComposerMode
  canPublish: boolean
  placeholder: string
  showPublishHint: boolean
  showSamanthaHint: boolean
}

export type ContextualComposerIntent =
  | 'ask_samantha'
  | 'publish'
  | 'ambiguous'

export type ContextualComposerIntentResult = {
  intent: ContextualComposerIntent

  contributionType:
    | EncounterContributionType
    | null

  confidence: number

  reason:
    | 'explicit_samantha'
    | 'explicit_publish'
    | 'publish_signal'
    | 'samantha_signal'
    | 'course_context'
    | 'non_publish_context'
    | 'unclear'
}

export type ContextualComposerOverride =
  | 'auto'
  | 'publish'
  | 'samantha'

export function getContextualComposerConfig(
  context: SamanthaPageContext,
  masterAgentName = 'Samantha'
): ContextualComposerConfig {
  if (
    context.kind === 'space' ||
    context.kind === 'encounter'
  ) {
    return {
      mode: 'hybrid',
      canPublish: true,

      placeholder:
        context.kind === 'encounter'
          ? `Aporta a este Encuentro o pregúntale a ${masterAgentName}…`
          : `Comparte algo aquí o pregúntale a ${masterAgentName}…`,

      showPublishHint: true,
      showSamanthaHint: true,
    }
  }

  if (context.kind === 'course') {
    return {
      mode: 'samantha',
      canPublish: false,

      placeholder:
        `Pregúntale a ${masterAgentName} sobre este contenido…`,

      showPublishHint: false,
      showSamanthaHint: true,
    }
  }

  return {
    mode: 'samantha',
    canPublish: false,
    placeholder:
      `${masterAgentName}, ¿qué vamos a crear hoy?`,
    showPublishHint: false,
    showSamanthaHint: true,
  }
}

/* -------------------------------------------------------------------------- */
/*                               NORMALIZATION                                */
/* -------------------------------------------------------------------------- */

function normalizeIntentText(
  input: string
) {
  return String(input || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(
      /[\u0300-\u036f]/g,
      ''
    )
}

/* -------------------------------------------------------------------------- */
/*                         CONTRIBUTION CLASSIFIER                            */
/* -------------------------------------------------------------------------- */

function resolveContributionType(
  text: string
): EncounterContributionType | null {
  const value =
    normalizeIntentText(text)

  /*
   * RESULT
   */
  if (
    [
      'me funciono',
      'me dio resultado',
      'consegui',
      'logre',
      'obtuvimos',
      'convirtio',
      'vendimos',
      'resultado',
      'resultados',
      'probe esto',
      'lo probe',
      'despues de probar',
    ].some(signal =>
      value.includes(signal)
    )
  ) {
    return 'result'
  }

  /*
   * RESOURCE
   */
  if (
    [
      'les comparto',
      'comparto esta',
      'comparto este',
      'plantilla',
      'documento',
      'archivo',
      'recurso',
      'guia',
      'checklist',
      'tutorial',
      'link',
      'enlace',
    ].some(signal =>
      value.includes(signal)
    )
  ) {
    return 'resource'
  }

  /*
   * IDEA
   */
  if (
    [
      'se me ocurre',
      'propongo',
      'podriamos',
      'podemos probar',
      'una idea',
      'mi idea',
      'sugiero',
      'que tal si',
    ].some(signal =>
      value.includes(signal)
    )
  ) {
    return 'idea'
  }

  /*
   * QUESTION
   *
   * Esto solo clasifica tipo.
   * NO significa automáticamente
   * que toda pregunta deba publicarse.
   */
  if (
    value.startsWith('alguien sabe') ||
    value.startsWith('alguien ha') ||
    value.startsWith('ustedes saben') ||
    value.includes(
      'que opinan'
    ) ||
    value.includes(
      'que piensan'
    ) ||
    value.includes(
      'alguien ha probado'
    )
  ) {
    return 'question'
  }

  return null
}

/* -------------------------------------------------------------------------- */
/*                              INTENT ROUTER                                 */
/* -------------------------------------------------------------------------- */

export function resolveContextualComposerIntent(args: {
  text: string

  context:
    SamanthaPageContext

  masterAgentName?:
    string

  override?:
    ContextualComposerOverride
}): ContextualComposerIntentResult {
  const {
    text,
    context,
    masterAgentName =
      'Samantha',
    override = 'auto',
  } = args

  const value =
    normalizeIntentText(text)

  /*
   * Contextos donde publicar
   * ni siquiera es opción.
   */
  if (
    context.kind !== 'space' &&
    context.kind !== 'encounter'
  ) {
    return {
      intent:
        'ask_samantha',

      contributionType:
        null,

      confidence: 1,

      reason:
        context.kind ===
        'course'
          ? 'course_context'
          : 'non_publish_context',
    }
  }

  /*
   * Override explícito del usuario.
   */
  if (
    override ===
    'samantha'
  ) {
    return {
      intent:
        'ask_samantha',

      contributionType:
        null,

      confidence: 1,

      reason:
        'explicit_samantha',
    }
  }

  if (
    override ===
    'publish'
  ) {
    return {
      intent:
        'publish',

      contributionType:
        resolveContributionType(
          text
        ),

      confidence: 1,

      reason:
        'explicit_publish',
    }
  }

  /*
   * Señales fuertes hacia Samantha.
   */
  const normalizedAgentName =
    normalizeIntentText(
      masterAgentName
    )

  const samanthaSignals = [
    `${normalizedAgentName} `,
    `${normalizedAgentName},`,
    'ayudame',
    'explicame',
    'recomiendame',
    'que me recomiendas',
    'que deberia hacer',
    'puedes ayudarme',
    'puedes explicarme',
    'resumeme',
    'analiza esto',
    'dime como',
  ]

  if (
    samanthaSignals.some(
      signal =>
        value.startsWith(
          signal
        ) ||
        value.includes(
          signal
        )
    )
  ) {
    return {
      intent:
        'ask_samantha',

      contributionType:
        null,

      confidence:
        0.96,

      reason:
        'samantha_signal',
    }
  }

  const contributionType =
    resolveContributionType(
      text
    )

  /*
   * Resultado/Recurso/Idea tienen
   * intención pública bastante fuerte
   * dentro de Space/Encounter.
   */
  if (
    contributionType ===
      'result' ||
    contributionType ===
      'resource' ||
    contributionType ===
      'idea'
  ) {
    return {
      intent:
        'publish',

      contributionType,

      confidence:
        0.88,

      reason:
        'publish_signal',
    }
  }

  /*
   * Pregunta comunitaria explícita.
   */
  if (
    contributionType ===
    'question'
  ) {
    return {
      intent:
        'publish',

      contributionType:
        'question',

      confidence:
        0.82,

      reason:
        'publish_signal',
    }
  }

  /*
   * No estamos seguros.
   *
   * NO enviamos a Samantha ni
   * publicamos automáticamente.
   */
  return {
    intent:
      'ambiguous',

    contributionType:
      null,

    confidence:
      0.4,

    reason:
      'unclear',
  }
}